import React from 'react';
import {ServiceListing} from '../constants';
import HomeModalShell from '../../home/components/HomeModalShell';
import {getAssetUrl} from '../../../../utils/assets';
import {
  formatServiceDeadline,
  getInitials,
  getServiceContactName,
  getServiceDisplayName,
  getServiceIconUrl,
  servicePlaceholderImageUrl,
} from '../serviceDisplay';
import styles from '../Services.module.css';

interface ServiceDetailsModalProps {
  isOpen: boolean;
  service: ServiceListing | null;
  onClose: () => void;
  onBookService: (service: ServiceListing) => void;
  onContactProvider: (service: ServiceListing) => void;
  onLearnMore: (service: ServiceListing) => void;
}

interface DetailItem {
  label: string;
  value: string;
  iconFile?: string;
}

const ServiceDetailIcon: React.FC<{fileName: string}> = ({fileName}) => (
  <span
    className={styles.serviceDetailIcon}
    style={{'--services-detail-icon-url': `url("${getServiceIconUrl(fileName)}")`} as React.CSSProperties}
    aria-hidden="true"
  />
);

const ServiceDetailsModal: React.FC<ServiceDetailsModalProps> = ({
  isOpen,
  service,
  onClose,
  onBookService,
  onContactProvider,
  onLearnMore,
}) => {
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const [previewImageFailed, setPreviewImageFailed] = React.useState(false);
  const [avatarImageFailed, setAvatarImageFailed] = React.useState(false);

  const servicePreviewUrl = getAssetUrl(service?.previewImageUrl);
  const profilePictureUrl = getAssetUrl(service?.providerProfilePicture);

  React.useEffect(() => {
    setPreviewImageFailed(false);
    setAvatarImageFailed(false);
  }, [service?._id, servicePreviewUrl, profilePictureUrl]);

  React.useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => closeButtonRef.current?.focus({preventScroll: true}), 0);
    }
  }, [isOpen, service?._id]);

  if (!isOpen || !service) {
    return null;
  }

  const isCollegeService = service.serviceKind === 'college';
  const displayName = getServiceDisplayName(service);
  const contactName = getServiceContactName(service);
  const showProfilePicture = Boolean(profilePictureUrl && !avatarImageFailed);
  const previewImageUrl = servicePreviewUrl && !previewImageFailed
    ? servicePreviewUrl
    : servicePlaceholderImageUrl;
  const priceValue = service.price || 'Price on request';
  const formattedDeadline = formatServiceDeadline(service.deadline);
  const summary = service.summary || 'Reach out to learn more about this service.';
  const detailItems: DetailItem[] = [];

  if (service.location) {
    detailItems.push({label: 'Location', value: service.location, iconFile: 'location-line.svg'});
  }

  if (service.availability) {
    detailItems.push({label: 'Availability', value: service.availability, iconFile: 'clock-line.svg'});
  }

  if (formattedDeadline) {
    detailItems.push({label: 'Deadline', value: formattedDeadline, iconFile: 'clock-line.svg'});
  }

  if (service.opportunityType) {
    detailItems.push({label: 'Opportunity', value: service.opportunityType});
  }

  if ((service.contactName || isCollegeService) && contactName !== displayName) {
    detailItems.push({label: 'Contact', value: contactName});
  }

  const visibleTags = service.tags.filter(Boolean);

  const handlePrimaryAction = () => {
    if (isCollegeService) {
      if (service.learnMoreUrl) {
        onLearnMore(service);
        return;
      }

      onContactProvider(service);
      return;
    }

    onBookService(service);
  };

  return (
    <HomeModalShell
      backdropClassName={styles.serviceModalBackdrop}
      panelClassName={styles.serviceModalCard}
      ariaLabelledBy="service-details-modal-title"
      ariaDescribedBy="service-details-modal-description"
      onClose={onClose}
    >
      <div className={styles.serviceModalHeader}>
        <div className={styles.serviceModalHeading}>
          <span className={styles.serviceModalKind}>{service.serviceKindLabel}</span>
          <h2 id="service-details-modal-title" className={styles.serviceModalTitle}>
            {service.title}
          </h2>
          <p id="service-details-modal-description" className={styles.serviceModalDescription}>
            {summary}
          </p>
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          className={styles.serviceModalClose}
          onClick={onClose}
          aria-label="Close service details"
        >
          &times;
        </button>
      </div>

      <div className={styles.serviceModalBody}>
        <div className={styles.serviceModalPreviewWrap}>
          <img
            src={previewImageUrl}
            alt=""
            aria-hidden="true"
            className={styles.serviceModalPreviewImage}
            onError={() => setPreviewImageFailed(true)}
          />
        </div>

        <div className={styles.serviceModalContent}>
          <div className={styles.serviceModalProviderCard}>
            <div className={styles.serviceModalAvatar}>
              {showProfilePicture ? (
                <img
                  src={profilePictureUrl}
                  alt={`${displayName} avatar`}
                  className={styles.serviceModalAvatarImage}
                  onError={() => setAvatarImageFailed(true)}
                />
              ) : (
                <span>{getInitials(displayName)}</span>
              )}
            </div>
            <div className={styles.serviceModalProviderInfo}>
              <span className={styles.serviceModalProviderName}>{displayName}</span>
              <span className={styles.serviceModalProviderMeta}>
                {isCollegeService && contactName !== displayName ? `Contact: ${contactName}` : service.serviceKindLabel}
              </span>
            </div>
          </div>

          <div className={styles.serviceModalPricePanel}>
            <span className={styles.serviceModalPriceLabel}>Price</span>
            <strong className={styles.serviceModalPriceValue}>{priceValue}</strong>
          </div>

          {detailItems.length > 0 ? (
            <dl className={styles.serviceModalDetailGrid}>
              {detailItems.map((item) => (
                <div key={`${item.label}-${item.value}`} className={styles.serviceModalDetailItem}>
                  <dt>
                    {item.iconFile ? <ServiceDetailIcon fileName={item.iconFile} /> : null}
                    <span>{item.label}</span>
                  </dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          {visibleTags.length > 0 ? (
            <div className={styles.serviceModalTags} aria-label="Service tags">
              {visibleTags.map((tag) => (
                <span key={tag} className={styles.serviceModalTag}>{tag}</span>
              ))}
            </div>
          ) : null}

          {service.eligibility ? (
            <section className={styles.serviceModalSection}>
              <h3>Eligibility</h3>
              <p>{service.eligibility}</p>
            </section>
          ) : null}
        </div>
      </div>

      <div className={styles.serviceModalActions}>
        <button
          type="button"
          className={`${styles.viewDetailsButton} ${styles.serviceModalMessageButton}`}
          onClick={() => onContactProvider(service)}
        >
          Message provider
        </button>
        {isCollegeService && service.learnMoreUrl ? (
          <a
            href={service.learnMoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.serviceModalPrimaryButton}
            onClick={handlePrimaryAction}
          >
            Book now
          </a>
        ) : (
          <button
            type="button"
            className={styles.serviceModalPrimaryButton}
            onClick={handlePrimaryAction}
          >
            Book now
          </button>
        )}
      </div>
    </HomeModalShell>
  );
};

export default React.memo(ServiceDetailsModal);
